import React, { useState, useEffect, useCallback } from 'react';
import { getPerformance, addLedgerEntry, deleteLedgerEntry } from '../services/apiService';
import '../styles/PerformancePanel.css';

const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const rs = (v) => (v == null ? '—' : `₹${Math.round(Number(v)).toLocaleString('en-IN')}`);
const signedRs = (v) => (v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}₹${Math.abs(Math.round(Number(v))).toLocaleString('en-IN')}`);
const pct = (v, d = 2) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(d)}%`);
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const tone = (v) => (v == null || v === 0 ? '' : v > 0 ? 'pf-pos' : 'pf-neg');
const hhmm = (iso) => (iso ? String(iso).slice(11, 16) : '—');
const day = (iso) => (iso ? String(iso).slice(0, 10) : '—');
const hold = (m) => (m == null ? '—' : m >= 1440 ? `${(m / 1440).toFixed(1)}d` : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

const TABS = ['Account', 'Trades', 'Daily', 'Quality', 'Capital ledger'];

const Stat = ({ label, value, cls = '', hint }) => (
  <div className="pf-stat" title={hint || ''}>
    <div className="pf-stat-label">{label}</div>
    <div className={`pf-stat-value ${cls}`}>{value}</div>
  </div>
);

const toCsv = (rows) => {
  const cols = ['entryTime', 'exitTime', 'symbol', 'kind', 'dir', 'strike', 'expiry', 'lots', 'qty', 'entryPrice', 'exitPrice',
    'capitalRs', 'grossRs', 'chargesRs', 'netRs', 'netR', 'pctOnCapital', 'pctOfAccount', 'holdMin', 'exitReason', 'outcome'];
  const esc = (v) => (v == null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  return [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
};

/**
 * Performance tab: returns on the money in the account (opening + deposits − withdrawals), not on
 * premium turned over. Admins switch between the engine's SYSTEM book and their own; others see
 * their own book only.
 */
const PerformancePanel = ({ currentUser, onClose }) => {
  const isAdmin = currentUser?.role === 'ADMIN';
  const [owner, setOwner] = useState(isAdmin ? 'SYSTEM' : '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tab, setTab] = useState('Account');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ kind: 'DEPOSIT', date: todayIST(), amountRs: '', note: '' });
  const [formMsg, setFormMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getPerformance(currentUser.username, { owner: owner || undefined, from, to }));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load performance.');
    } finally {
      setLoading(false);
    }
  }, [currentUser.username, owner, from, to]);

  useEffect(() => { load(); }, [load]);

  const canEditLedger = isAdmin || owner !== 'SYSTEM';

  const submitLedger = async (e) => {
    e.preventDefault();
    setFormMsg('');
    const amount = Number(form.amountRs);
    if (!(amount > 0)) { setFormMsg('Enter a positive amount.'); return; }
    try {
      await addLedgerEntry(currentUser.username, owner || undefined, { ...form, amountRs: amount });
      setForm(f => ({ ...f, amountRs: '', note: '' }));
      load();
    } catch (err) {
      setFormMsg(err?.response?.data?.error || 'Could not save the entry.');
    }
  };

  const removeLedger = async (id) => {
    if (!window.confirm('Delete this ledger entry?')) return;
    try { await deleteLedgerEntry(currentUser.username, id); load(); }
    catch (err) { setFormMsg(err?.response?.data?.error || 'Could not delete the entry.'); }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(data?.trades || [])], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `trades-${owner || currentUser.username}-${todayIST()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const acc = data?.account;
  const q = data?.quality;

  return (
    <div className="pf-overlay" onClick={onClose}>
      <div className="pf-modal" onClick={e => e.stopPropagation()}>
        <div className="pf-header">
          <span>📈 Performance</span>
          <button className="pf-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="pf-toolbar">
          {isAdmin && (
            <div className="pf-switch">
              <button className={owner === 'SYSTEM' ? 'active' : ''} onClick={() => setOwner('SYSTEM')}>Engine (SYSTEM)</button>
              <button className={owner !== 'SYSTEM' ? 'active' : ''} onClick={() => setOwner('')}>My trades</button>
            </div>
          )}
          <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label>To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
          {(from || to) && <button className="pf-btn" onClick={() => { setFrom(''); setTo(''); }}>All time</button>}
          <button className="pf-btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : '↻ Refresh'}</button>
        </div>

        <div className="pf-tabs">
          {TABS.map(t => (
            <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        {error && <div className="pf-error">{error}</div>}
        {!data && loading && <div className="pf-empty">Loading…</div>}

        {data && tab === 'Account' && acc && (
          <div>
            {acc.impliedOpening && (
              <div className="pf-note">
                No opening balance recorded — assuming {rs(acc.openingRs)} from the first trade. Add the real figure under Capital ledger.
              </div>
            )}
            <div className="pf-grid">
              <Stat label="Account value" value={rs(acc.equityRs)} hint="Opening + deposits − withdrawals + net P&L" />
              <Stat label="Money put in (net)" value={rs(acc.netInvestedRs)} hint="Opening + deposits − withdrawals" />
              <Stat label="Net P&L (after charges)" value={signedRs(acc.netPnlRs)} cls={tone(acc.netPnlRs)} />
              <Stat label="Return (time-weighted)" value={pct(acc.returnPct)} cls={tone(acc.returnPct)}
                    hint="Each day's net ÷ that day's starting account value, chained; deposits and withdrawals don't count as gains or losses" />
              <Stat label="Max drawdown" value={`${pct(acc.maxDrawdownPct)} (${signedRs(acc.maxDrawdownRs)})`} cls={tone(acc.maxDrawdownRs)}
                    hint="Largest fall in P&L from a previous high" />
              <Stat label="Today" value={signedRs(acc.todayPnlRs)} cls={tone(acc.todayPnlRs)} />
              <Stat label="This week" value={signedRs(acc.weekPnlRs)} cls={tone(acc.weekPnlRs)} />
              <Stat label="This month" value={signedRs(acc.monthPnlRs)} cls={tone(acc.monthPnlRs)} />
              <Stat label="Opening" value={rs(acc.openingRs)} />
              <Stat label="Deposits" value={rs(acc.depositsRs)} />
              <Stat label="Withdrawals" value={rs(acc.withdrawalsRs)} />
              <Stat label="Open positions" value={acc.openTrades ?? 0} />
            </div>
            {q && (
              <div className="pf-grid pf-grid-small">
                <Stat label="Trades" value={q.trades} />
                <Stat label="Win rate" value={q.winRatePct == null ? '—' : `${num(q.winRatePct, 1)}%`} />
                <Stat label="Profit factor" value={num(q.profitFactor)} hint="Gross wins ÷ gross losses" />
                <Stat label="Expectancy / trade" value={`${signedRs(q.expectancyRs)} (${num(q.expectancyR)}R)`} cls={tone(q.expectancyRs)} />
                <Stat label="Charges paid" value={rs(q.chargesRs)} />
                <Stat label="Green days" value={q.tradingDays ? `${q.greenDays} / ${q.tradingDays}` : '—'} />
              </div>
            )}
            <div className="pf-foot">Account figures are all-time; the date filter applies to the trades, daily and quality tabs.</div>
          </div>
        )}

        {data && tab === 'Trades' && (
          <div>
            <div className="pf-tablebar">
              <span>{data.trades.length} trades</span>
              <button className="pf-btn" onClick={exportCsv} disabled={!data.trades.length}>⬇ CSV</button>
            </div>
            <div className="pf-table-wrap">
              <table className="pf-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Symbol</th><th>Type</th><th>Strike</th><th>Lots</th>
                    <th>Entry</th><th>Exit</th><th>Hold</th><th>Capital used</th><th>Charges</th>
                    <th>Net P&L</th><th>R</th><th>% on capital</th><th>% of account</th><th>Exit reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trades.length === 0 && <tr><td colSpan={15} className="pf-empty">No trades in this range.</td></tr>}
                  {data.trades.map(t => (
                    <tr key={t.id} className={t.exitTime ? '' : 'pf-open'}>
                      <td>{day(t.entryTime)}</td>
                      <td>{t.symbol}</td>
                      <td>{t.kind}{t.dir ? ` · ${String(t.dir).toUpperCase()}` : ''}</td>
                      <td>{t.strike ?? '—'}</td>
                      <td>{t.lots ?? '—'}{t.qty ? <span className="pf-dim"> ({t.qty})</span> : null}</td>
                      <td>{hhmm(t.entryTime)} @ {num(t.entryPrice)}</td>
                      <td>{t.exitTime ? `${day(t.exitTime) !== day(t.entryTime) ? day(t.exitTime) + ' ' : ''}${hhmm(t.exitTime)} @ ${num(t.exitPrice)}` : 'open'}</td>
                      <td>{hold(t.holdMin)}</td>
                      <td>{rs(t.capitalRs)}</td>
                      <td>{rs(t.chargesRs)}</td>
                      <td className={tone(t.netRs)}>{signedRs(t.netRs)}</td>
                      <td className={tone(t.netR)}>{num(t.netR)}</td>
                      <td className={tone(t.pctOnCapital)}>{pct(t.pctOnCapital, 1)}</td>
                      <td className={tone(t.pctOfAccount)}>{pct(t.pctOfAccount)}</td>
                      <td className="pf-reason" title={t.exitReason || ''}>{t.exitReason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data && tab === 'Daily' && (
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th>Date</th><th>Trades</th><th>W / L</th><th>Start value</th><th>Money in/out</th>
                  <th>Net P&L</th><th>R</th><th>Return</th><th>Charges</th><th>Peak capital used</th><th>Utilisation</th><th>End value</th>
                </tr>
              </thead>
              <tbody>
                {data.daily.length === 0 && <tr><td colSpan={12} className="pf-empty">No closed trades in this range.</td></tr>}
                {[...data.daily].reverse().map(d => (
                  <tr key={d.date}>
                    <td>{d.date}</td>
                    <td>{d.trades}</td>
                    <td>{d.wins} / {d.losses}</td>
                    <td>{rs(d.startEquityRs)}</td>
                    <td className={tone(d.flowRs)}>{d.flowRs ? signedRs(d.flowRs) : '—'}</td>
                    <td className={tone(d.netRs)}>{signedRs(d.netRs)}</td>
                    <td className={tone(d.netR)}>{num(d.netR)}</td>
                    <td className={tone(d.returnPct)}>{pct(d.returnPct)}</td>
                    <td>{rs(d.chargesRs)}</td>
                    <td>{rs(d.peakCapitalRs)}</td>
                    <td>{d.utilisationPct == null ? '—' : `${num(d.utilisationPct, 1)}%`}</td>
                    <td>{rs(d.endEquityRs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pf-foot">Utilisation = most money tied up in open positions at once ÷ the day's starting account value.</div>
          </div>
        )}

        {data && tab === 'Quality' && q && (
          <div>
            <div className="pf-grid">
              <Stat label="Trades" value={q.trades} />
              <Stat label="Wins / losses" value={`${q.wins} / ${q.losses}`} />
              <Stat label="Win rate" value={q.winRatePct == null ? '—' : `${num(q.winRatePct, 1)}%`} />
              <Stat label="Net P&L" value={signedRs(q.netPnlRs)} cls={tone(q.netPnlRs)} />
              <Stat label="Avg win" value={`${signedRs(q.avgWinRs)} (${num(q.avgWinR)}R)`} cls="pf-pos" />
              <Stat label="Avg loss" value={`${signedRs(q.avgLossRs)} (${num(q.avgLossR)}R)`} cls="pf-neg" />
              <Stat label="Profit factor" value={num(q.profitFactor)} hint="Gross wins ÷ gross losses; above 1 makes money" />
              <Stat label="Expectancy / trade" value={`${signedRs(q.expectancyRs)} (${num(q.expectancyR)}R)`} cls={tone(q.expectancyRs)} />
              <Stat label="Longest losing streak" value={q.longestLosingStreak} />
              <Stat label="Avg hold" value={hold(q.avgHoldMin == null ? null : Math.round(q.avgHoldMin))} />
              <Stat label="Best day" value={q.bestDay ? `${q.bestDay.date} ${signedRs(q.bestDay.netRs)}` : '—'} cls="pf-pos" />
              <Stat label="Worst day" value={q.worstDay ? `${q.worstDay.date} ${signedRs(q.worstDay.netRs)}` : '—'} cls="pf-neg" />
            </div>
            {q.byStrategy && Object.keys(q.byStrategy).length > 0 && (
              <table className="pf-table pf-table-narrow">
                <thead><tr><th>Strategy</th><th>Trades</th><th>Wins</th><th>Net P&L</th></tr></thead>
                <tbody>
                  {Object.entries(q.byStrategy).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td>{v.trades}</td><td>{v.wins}</td><td className={tone(v.netRs)}>{signedRs(v.netRs)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {data && tab === 'Capital ledger' && (
          <div>
            <div className="pf-note">
              Record the money in the account: an <b>opening</b> balance, then each <b>deposit</b> or <b>withdrawal</b>.
              Returns are measured on this money, so a deposit is never counted as profit and a withdrawal never as a loss.
            </div>
            {canEditLedger && (
              <form className="pf-form" onSubmit={submitLedger}>
                <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}>
                  <option value="OPENING">Opening balance</option>
                  <option value="DEPOSIT">Deposit</option>
                  <option value="WITHDRAWAL">Withdrawal</option>
                </select>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
                <input type="number" min="1" step="1" placeholder="Amount ₹" value={form.amountRs}
                       onChange={e => setForm(f => ({ ...f, amountRs: e.target.value }))} required />
                <input type="text" maxLength={200} placeholder="Note (optional)" value={form.note}
                       onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                <button className="pf-btn pf-btn-primary" type="submit">Add</button>
              </form>
            )}
            {formMsg && <div className="pf-error">{formMsg}</div>}
            <table className="pf-table pf-table-narrow">
              <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Note</th><th>By</th>{canEditLedger && <th />}</tr></thead>
              <tbody>
                {data.ledger.map(l => (
                  <tr key={l.id ?? 'assumed'} className={l.id == null ? 'pf-assumed' : ''}>
                    <td>{l.date}</td>
                    <td>{l.kind === 'OPENING' ? 'Opening' : l.kind === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'}</td>
                    <td className={l.kind === 'WITHDRAWAL' ? 'pf-neg' : ''}>{l.kind === 'WITHDRAWAL' ? '−' : ''}{rs(l.amountRs)}</td>
                    <td>{l.id == null ? 'Assumed — add the real opening balance above' : l.note || ''}</td>
                    <td className="pf-dim">{l.createdBy || ''}</td>
                    {canEditLedger && (
                      <td>{l.id != null && <button className="pf-del" onClick={() => removeLedger(l.id)} title="Delete">✕</button>}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformancePanel;
